import { clsx, type ClassValue } from 'clsx';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function formatPrice(priceInPaise: number): string {
  const amount = priceInPaise / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPriceRange(min: number | null, max: number | null, unit = 'INR'): string {
  if (!min && !max) return 'Price on Request';

  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: unit,
    maximumFractionDigits: 0,
    notation: 'compact',
  });

  if (min && max) {
    if (min === max) return formatter.format(min);
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  }

  if (min) return `From ${formatter.format(min)}`;
  if (max) return `Up to ${formatter.format(max)}`;

  return 'Price on Request';
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  return `${hours}h ${mins}m`;
}

export function formatArea(min: number | null, max: number | null, unit = 'sqft'): string {
  if (!min && !max) return '';

  if (min && max) {
    if (min === max) return `${min.toLocaleString('en-IN')} ${unit}`;
    return `${min.toLocaleString('en-IN')} - ${max.toLocaleString('en-IN')} ${unit}`;
  }

  if (min) return `From ${min.toLocaleString('en-IN')} ${unit}`;
  if (max) return `Up to ${max.toLocaleString('en-IN')} ${unit}`;

  return '';
}

export function formatBedrooms(min: number | null, max: number | null): string {
  if (!min && !max) return '';

  if (min && max) {
    if (min === max) return `${min} BHK`;
    return `${min} - ${max} BHK`;
  }

  if (min) return `${min}+ BHK`;
  if (max) return `Up to ${max} BHK`;

  return '';
}

export function getDayName(dayOfWeek: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayOfWeek] || '';
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'confirmed':
      return 'bg-green-100 text-green-800';
    case 'completed':
      return 'bg-blue-100 text-blue-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'no_show':
      return 'bg-gray-100 text-gray-800';
    case 'captured':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'refunded':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getProjectTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    apartment: 'Apartment',
    villa: 'Villa',
    plot: 'Plot',
    commercial: 'Commercial',
  };
  return labels[type] || type;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function debounce<T extends (...args: unknown[]) => unknown>(fn: T, delay: number): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function formatPossessionDate(dateStr: string | null): string {
  if (!dateStr) return 'Ready to Move';
  const date = new Date(dateStr);
  if (date <= new Date()) return 'Ready to Move';
  return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}
